import React,{useCallback,useEffect,useRef,useState}from"react";
import{Box,Button,Chip,CircularProgress,Paper,TextField,Typography}from"@mui/material";
import DeleteOutlineOutlined from"@mui/icons-material/DeleteOutlineOutlined";
import PhotoCameraOutlined from"@mui/icons-material/PhotoCameraOutlined";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

export default function AdminProfile(){
  const{authFetch}=useAuth();
  const{showSuccess,showError,showWarning}=useAlert();
  const fileRef=useRef(null);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[deleting,setDeleting]=useState(false);
  const[info,setInfo]=useState(null);
  const[file,setFile]=useState(null);
  const[preview,setPreview]=useState("");

  const loadData=useCallback(async()=>{
    setLoading(true);
    try{
      const response=await authFetch("/api/login/administrator/info/");
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudo cargar la información de la empresa.");
      setInfo(data);
      setPreview(data.logo||"");
      setFile(null);
    }catch(error){
      showError(error.message||"No se pudo cargar la información de la empresa.");
    }finally{
      setLoading(false);
    }
  },[authFetch,showError]);

  useEffect(()=>{loadData();},[loadData]);

  useEffect(()=>{
    return()=>{
      if(preview&&preview.startsWith("blob:"))URL.revokeObjectURL(preview);
    };
  },[preview]);

  const selectFile=(event)=>{
    const selected=event.target.files?.[0];
    if(!selected)return;

    const allowed=["image/jpeg","image/png","image/webp","image/heic","image/heif","image/heic-sequence","image/heif-sequence"];
    const extension=selected.name.split(".").pop()?.toLowerCase();
    const allowedExtensions=["jpg","jpeg","png","webp","heic","heif"];

    if(!allowed.includes(selected.type)&&!allowedExtensions.includes(extension)){
      showWarning("Seleccione una imagen JPG, JPEG, PNG, WEBP, HEIC o HEIF.");
      event.target.value="";
      return;
    }

    if(selected.size>10*1024*1024){
      showWarning("La imagen no puede superar los 10 MB.");
      event.target.value="";
      return;
    }

    if(preview&&preview.startsWith("blob:"))URL.revokeObjectURL(preview);

    setFile(selected);

    if(selected.type==="image/heic"||selected.type==="image/heif"||extension==="heic"||extension==="heif"){
      setPreview("");
    }else{
      setPreview(URL.createObjectURL(selected));
    }
  };

  const uploadLogo=async()=>{
    if(!file){
      showWarning("Seleccione una imagen para el logo.");
      return;
    }

    setSaving(true);

    try{
      const formData=new FormData();
      formData.append("logo",file);

      const response=await authFetch("/api/login/administrator/info/",{
        method:"PATCH",
        body:formData,
      });

      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo guardar el logo.");

      if(preview&&preview.startsWith("blob:"))URL.revokeObjectURL(preview);

      const administrator=data.administrator||data;

      setInfo(administrator);
      setPreview(administrator.logo||"");
      setFile(null);

      if(fileRef.current)fileRef.current.value="";

      showSuccess(data.detail||"Logo actualizado correctamente.");
    }catch(error){
      showError(error.message||"No se pudo guardar el logo.");
    }finally{
      setSaving(false);
    }
  };

  const deleteLogo=async()=>{
    if(!info?.logo&&!preview){
      showWarning("La empresa no tiene un logo registrado.");
      return;
    }

    setDeleting(true);

    try{
      const response=await authFetch("/api/login/administrator/info/",{
        method:"DELETE",
      });

      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo eliminar el logo.");

      if(preview&&preview.startsWith("blob:"))URL.revokeObjectURL(preview);

      const administrator=data.administrator||info;

      setInfo({...administrator,logo:null});
      setPreview("");
      setFile(null);

      if(fileRef.current)fileRef.current.value="";

      showSuccess(data.detail||"Logo eliminado correctamente.");
    }catch(error){
      showError(error.message||"No se pudo eliminar el logo.");
    }finally{
      setDeleting(false);
    }
  };

  const fieldSx={
    "& .MuiOutlinedInput-root":{borderRadius:2,bgcolor:"#f8fafc"},
    "& .MuiInputBase-input.Mui-disabled":{WebkitTextFillColor:"#344054"},
    "& .MuiInputLabel-root.Mui-disabled":{color:"#667085"},
  };

  if(loading){
    return(
      <Box sx={{display:"flex",alignItems:"center",justifyContent:"center",py:8}}>
        <CircularProgress size={30} sx={{color:"#123f6d"}}/>
      </Box>
    );
  }

  if(!info){
    return(
      <Box>
        <Typography sx={{fontSize:24,fontWeight:700,color:"#182230",mb:2.5}}>Información de la empresa</Typography>
        <Paper variant="outlined" sx={{borderRadius:3,borderColor:"#dce3ea",boxShadow:"none",p:4}}>
          <Typography sx={{fontSize:14,color:"#667085",textAlign:"center"}}>No se pudo cargar la información de la empresa.</Typography>
        </Paper>
      </Box>
    );
  }

  const supervisors=Array.isArray(info.supervisors)?info.supervisors:[];

  return(
    <Box>
      <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,mb:2.5}}>
        <Typography sx={{fontSize:24,fontWeight:700,color:"#182230"}}>Información de la empresa</Typography>
        <Chip
          label={info.active?"Activo":"Inactivo"}
          size="small"
          sx={{
            bgcolor:info.active?"#e9f7ef":"#fdecec",
            color:info.active?"#207a45":"#b42318",
            fontSize:12,
            fontWeight:700,
          }}
        />
      </Box>

      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",lg:"280px minmax(0,1fr)"},gap:2.5,alignItems:"start"}}>
        <Paper variant="outlined" sx={{borderRadius:3,borderColor:"#dce3ea",boxShadow:"none",p:2.5}}>
          <Typography sx={{fontSize:15,fontWeight:700,color:"#182230",mb:2}}>Logo de la empresa</Typography>

          <Box sx={{width:"100%",aspectRatio:"1 / 1",border:"1px solid #e4e7ec",borderRadius:3,bgcolor:"#f8fafc",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",mb:2}}>
            {preview?(
              <Box component="img" src={preview} alt={info.company_name||"Logo de la empresa"} sx={{width:"100%",height:"100%",objectFit:"contain",p:2,boxSizing:"border-box"}}/>
            ):(
              <Box sx={{textAlign:"center",px:2}}>
                <PhotoCameraOutlined sx={{fontSize:38,color:"#98a2b3",mb:1}}/>
                <Typography sx={{fontSize:13,color:"#667085"}}>Sin logo</Typography>
              </Box>
            )}
          </Box>

          {file&&(
            <Box sx={{mb:2,p:1.2,bgcolor:"#f8fafc",borderRadius:2,border:"1px solid #e4e7ec"}}>
              <Typography sx={{fontSize:12.5,color:"#344054",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{file.name}</Typography>
              <Typography sx={{fontSize:11.5,color:"#98a2b3",mt:.3}}>{(file.size/1024/1024).toFixed(2)} MB</Typography>
            </Box>
          )}

          <input
            ref={fileRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
            hidden
            onChange={selectFile}
          />

          <Button
            fullWidth
            variant="outlined"
            startIcon={<PhotoCameraOutlined/>}
            onClick={()=>fileRef.current?.click()}
            disabled={saving||deleting}
            sx={{
              borderRadius:2,
              textTransform:"none",
              fontWeight:700,
              borderColor:"#b8c5d1",
              color:"#123f6d",
              mb:1,
            }}
          >
            Seleccionar imagen
          </Button>

          {file&&(
            <Button
              fullWidth
              variant="contained"
              onClick={uploadLogo}
              disabled={saving||deleting}
              sx={{
                bgcolor:"#123f6d",
                borderRadius:2,
                textTransform:"none",
                fontWeight:700,
                boxShadow:"none",
                mb:1,
                "&:hover":{bgcolor:"#0e345b",boxShadow:"none"},
              }}
            >
              {saving?"Guardando...":info.logo?"Actualizar logo":"Guardar logo"}
            </Button>
          )}

          {info.logo&&(
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DeleteOutlineOutlined/>}
              onClick={deleteLogo}
              disabled={saving||deleting}
              sx={{
                borderRadius:2,
                textTransform:"none",
                fontWeight:700,
                color:"#b42318",
                borderColor:"#f0b7b3",
                "&:hover":{borderColor:"#b42318",bgcolor:"#fff6f5"},
              }}
            >
              {deleting?"Eliminando...":"Eliminar logo"}
            </Button>
          )}

          <Typography sx={{fontSize:11.5,color:"#98a2b3",lineHeight:1.5,mt:2}}>
            Puede seleccionar imágenes JPG, JPEG, PNG, WEBP, HEIC o HEIF.
          </Typography>
        </Paper>

        <Box sx={{display:"flex",flexDirection:"column",gap:2.5}}>
          <Paper variant="outlined" sx={{borderRadius:3,borderColor:"#dce3ea",boxShadow:"none",p:{xs:2,md:3}}}>
            <Typography sx={{fontSize:16,fontWeight:700,color:"#182230",mb:2.5}}>Datos asignados</Typography>

            <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:2.5}}>
              <TextField label="Empresa" value={info.company_name||""} disabled size="small" fullWidth sx={fieldSx}/>
              <TextField label="RUC" value={info.ruc||""} disabled size="small" fullWidth sx={fieldSx}/>
              <TextField label="Representante" value={info.representative||""} disabled size="small" fullWidth sx={fieldSx}/>
              <TextField label="Teléfono" value={info.phone||""} disabled size="small" fullWidth sx={fieldSx}/>
              <TextField label="Correo" value={info.email||""} disabled size="small" fullWidth sx={fieldSx}/>
              <TextField label="Tipo de servicio" value={info.service_type||""} disabled size="small" fullWidth sx={fieldSx}/>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{borderRadius:3,borderColor:"#dce3ea",boxShadow:"none",p:{xs:2,md:3}}}>
            <Typography sx={{fontSize:16,fontWeight:700,color:"#182230",mb:2}}>Supervisores</Typography>

            {supervisors.length===0?(
              <Typography sx={{fontSize:13.5,color:"#667085"}}>Sin supervisores.</Typography>
            ):(
              <Box sx={{display:"flex",flexDirection:"column"}}>
                {supervisors.map((supervisor,index)=>(
                  <Box
                    key={supervisor.id}
                    sx={{
                      py:1.5,
                      display:"grid",
                      gridTemplateColumns:{xs:"1fr",md:"1.2fr 1.5fr 1fr"},
                      gap:{xs:.5,md:2},
                      alignItems:"center",
                      borderBottom:index<supervisors.length-1?"1px solid #edf1f5":"none",
                    }}
                  >
                    <Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>
                      {supervisor.name}
                    </Typography>

                    <Typography sx={{fontSize:13,color:"#667085",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {supervisor.email}
                    </Typography>

                    <Typography sx={{fontSize:13,color:"#667085"}}>
                      {supervisor.phone||"-"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}